import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { donate } from "../lib/donate";
import { Heart, CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { trackEvent } from "../lib/analytics";

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: null, data: null });

  useEffect(() => {
    if (!sid) { setState({ loading: false, status: "missing", data: null }); return; }
    let attempts = 0;
    let cancelled = false;
    async function poll() {
      try {
        const d = await donate.sessionStatus(sid);
        if (cancelled) return;
        if (d.payment_status === "paid") {
          trackEvent("donate_success", { amount: d.amount_total, currency: d.currency });
          setState({ loading: false, status: "paid", data: d });
          return;
        }
        if (d.status === "expired" || d.payment_status === "unpaid" && attempts > 8) {
          setState({ loading: false, status: "failed", data: d });
          return;
        }
        attempts += 1;
        if (attempts < 10) setTimeout(poll, 2000);
        else setState({ loading: false, status: "timeout", data: d });
      } catch {
        setState({ loading: false, status: "error", data: null });
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)", color: "var(--text-main)" }}>
      <div className="card-tactile rounded-2xl p-10 text-center max-w-lg">
        {state.loading ? (
          <>
            <Loader2 className="h-12 w-12 mx-auto animate-spin" style={{ color: "var(--primary)" }} />
            <h1 className="font-display font-black text-2xl mt-4">Confirming your donation…</h1>
            <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Just a moment while we check with Stripe.</p>
          </>
        ) : state.status === "paid" ? (
          <>
            <div className="h-14 w-14 rounded-full flex items-center justify-center mx-auto pulse-accent" style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)" }}>
              <Heart className="h-7 w-7" style={{ color: "var(--accent)" }} />
            </div>
            <h1 className="font-display font-black text-3xl mt-5">Thank you 💚</h1>
            <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>
              Your donation of <b>{(state.data.amount_total / 100).toFixed(2)} {state.data.currency?.toUpperCase()}</b> keeps VeriHandi free, open, and growing. We're genuinely grateful.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 px-5 h-11 rounded-lg font-semibold text-sm" style={{ background: "var(--primary)", color: "white" }}>
              Back to VeriHandi <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        ) : (
          <>
            <div className="h-14 w-14 rounded-full flex items-center justify-center mx-auto" style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)" }}>
              <XCircle className="h-7 w-7" style={{ color: "var(--accent)" }} />
            </div>
            <h1 className="font-display font-black text-2xl mt-5">Something didn't complete</h1>
            <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>No money was taken. You can try again any time.</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 px-5 h-11 rounded-lg font-semibold text-sm" style={{ background: "var(--primary)", color: "white" }}>
              Back to VeriHandi <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
